import { useCallback, useEffect, useState } from "react";
import { shadowParityApi } from "@/features/observability/api/shadow-parity.api";
import {
    ParitySummary,
    ParityTimeseries,
    ParityDivergencesOutput,
} from "@/features/observability/types/parity.types";

const REFRESH_MS = 30_000;

export type ShadowParityData = {
    summary: ParitySummary | null;
    timeseries: ParityTimeseries | null;
    divergences: ParityDivergencesOutput | null;
};

const EMPTY: ShadowParityData = {
    summary: null,
    timeseries: null,
    divergences: null,
};

const valueOrNull = <T>(result: PromiseSettledResult<T>): T | null =>
    result.status === "fulfilled" ? result.value : null;

export function useShadowParity() {
    const [data, setData] = useState<ShadowParityData>(EMPTY);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        let cancelled = false;
        // Effect-scoped guard: stops interval ticks stacking under a slow
        // cluster, without surviving StrictMode's mount/unmount/mount.
        let running = false;

        const load = async () => {
            if (running) return;
            running = true;
            setLoading(true);
            // allSettled: one failing endpoint must not blank the board; each
            // dataset degrades independently and keeps last-known values.
            const [summary, timeseries, divergences] = await Promise.allSettled([
                shadowParityApi.fetchSummary(),
                shadowParityApi.fetchTimeseries(),
                shadowParityApi.fetchDivergences(),
            ]);
            running = false;
            if (cancelled) return;

            setData((prev) => ({
                summary: valueOrNull(summary) ?? prev.summary,
                timeseries: valueOrNull(timeseries) ?? prev.timeseries,
                divergences: valueOrNull(divergences) ?? prev.divergences,
            }));
            // Summary is the core signal: only its failure is a hard error.
            if (summary.status === "fulfilled") {
                setError(false);
                setLastUpdatedAt(Date.now());
            } else {
                setError(true);
            }
            setLoading(false);
        };

        void load();
        const intervalId = setInterval(() => void load(), REFRESH_MS);
        return () => {
            cancelled = true;
            clearInterval(intervalId);
        };
    }, [reloadKey]);

    const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

    return { data, loading, error, lastUpdatedAt, refresh };
}
